"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PENDING_REVISION_FUND_CONFIRM_BODY = exports.PENDING_REVISION_FUND_CONFIRM_TITLE = exports.PENDING_REVISION_FUND_UNTOUCHED_BANNER = exports.PENDING_REVISION_FUND_TOUCHED_BANNER = void 0;
exports.isRevisionInvalidatedByLiveEvent = isRevisionInvalidatedByLiveEvent;
exports.revisionInvalidationNotice = revisionInvalidationNotice;
exports.revisionInvalidationPushNotifications = revisionInvalidationPushNotifications;
exports.evaluatePendingRevisionInvalidation = evaluatePendingRevisionInvalidation;
exports.pendingRevisionFundWarning = pendingRevisionFundWarning;
const revision_gating_js_1 = require("./revision-gating.js");
const revision_diff_js_1 = require("./revision-diff.js");
const revision_version_js_1 = require("./revision-version.js");
function touchedInvoiceIds(live, raw) {
    const meta = (0, revision_diff_js_1.normalizeRevisionV2Metadata)(raw);
    const ids = new Set();
    for (const change of meta.invoiceChanges) {
        if (change.id)
            ids.add(change.id);
    }
    // Include parent milestones touched only via submilestone add/edit/delete (same as fund_release).
    const touchedMilestones = touchedMilestoneIds(raw);
    for (const invoice of live.invoices) {
        const touchesChanged = invoice.milestoneIds.some((id) => touchedMilestones.has(id));
        const regrouped = meta.invoiceChanges.some((change) => change.id === invoice.id && change.milestoneIds != null);
        if (touchesChanged || regrouped)
            ids.add(invoice.id);
    }
    return ids;
}
function touchedMilestoneIds(raw) {
    const meta = (0, revision_diff_js_1.normalizeRevisionV2Metadata)(raw);
    const ids = new Set([
        ...meta.milestoneChanges.map((item) => item.id),
        ...meta.milestonesDeleted.map((item) => item.id),
    ]);
    for (const change of meta.submilestoneChanges) {
        if (change.parentMilestoneId)
            ids.add(change.parentMilestoneId);
    }
    for (const added of meta.submilestonesAdded) {
        if (added.parentMilestoneId)
            ids.add(added.parentMilestoneId);
    }
    for (const deleted of meta.submilestonesDeleted) {
        if (deleted.parentMilestoneId)
            ids.add(deleted.parentMilestoneId);
    }
    return ids;
}
function isRevisionInvalidatedByLiveEvent(live, raw, event) {
    // PRD 4.2.11: a dispute freezes a pending revision; it does not invalidate it.
    // Accept/request-changes stay blocked while disputing via live.disputing validation.
    if (event.type === "dispute_opened")
        return false;
    if (event.type === "invoice_paid") {
        if (!event.invoiceId)
            return false;
        return touchedInvoiceIds(live, raw).has(event.invoiceId);
    }
    if (event.type === "fund_release") {
        if (!event.milestoneId)
            return false;
        return touchedMilestoneIds(raw).has(event.milestoneId);
    }
    if (event.type === "money_state_stricter") {
        if (!event.milestoneId)
            return false;
        const meta = (0, revision_diff_js_1.normalizeRevisionV2Metadata)(raw);
        const change = meta.milestoneChanges.find((item) => item.id === event.milestoneId);
        const deleted = meta.milestonesDeleted.some((item) => item.id === event.milestoneId);
        if (!change && !deleted)
            return false;
        const current = live.milestones.find((milestone) => milestone.id === event.milestoneId);
        if (!current)
            return deleted;
        const nextState = current.moneyState;
        return (0, revision_gating_js_1.isMoneyStateStricter)("scheduled_unpaid", nextState) || deleted;
    }
    return false;
}
const NO_LONGER_VALID_TITLE = "Revision no longer valid";
function revisionInvalidationNotice(event) {
    if (event.type === "invoice_paid") {
        return {
            reason: "The client funded an invoice this proposal changes.",
            businessTitle: NO_LONGER_VALID_TITLE,
            businessMessage: "The client funded an invoice this proposal changes. Reopen it to rebuild against the current schedule.",
            clientTitle: NO_LONGER_VALID_TITLE,
            clientMessage: "You funded this invoice. The pending revision is no longer valid. Nothing else on the project changed. The business will send an updated proposal if needed.",
        };
    }
    if (event.type === "fund_release") {
        return {
            reason: "Funds were released on a milestone this proposal edits.",
            businessTitle: NO_LONGER_VALID_TITLE,
            businessMessage: "Funds were released on a milestone this proposal edits. Reopen it to rebuild against the current schedule.",
            clientTitle: NO_LONGER_VALID_TITLE,
            clientMessage: "You released funds on a milestone in a pending revision. That revision is no longer valid. Nothing else on the project changed. The business will send an updated proposal if needed.",
        };
    }
    if (event.type === "money_state_stricter") {
        return {
            reason: "The money state of an edited milestone became stricter.",
            businessTitle: NO_LONGER_VALID_TITLE,
            businessMessage: "The money state of an edited milestone became stricter. Reopen it to rebuild against the current schedule.",
            clientTitle: NO_LONGER_VALID_TITLE,
            clientMessage: "This project changed in a way that no longer matches the pending revision. Nothing is required from you. The business will send an updated proposal if needed.",
        };
    }
    return {
        reason: "A dispute was opened on this project.",
        businessTitle: NO_LONGER_VALID_TITLE,
        businessMessage: "A dispute was opened on this project.",
        clientTitle: NO_LONGER_VALID_TITLE,
        clientMessage: "A dispute was opened on this project.",
    };
}
function revisionInvalidationPushNotifications(input) {
    const notice = revisionInvalidationNotice(input.event);
    const rows = [];
    if (input.managerId) {
        rows.push({
            recipientId: input.managerId,
            recipientType: "business_user",
            title: notice.businessTitle,
            message: notice.businessMessage,
        });
    }
    if (input.clientId && input.clientId !== input.managerId) {
        rows.push({
            recipientId: input.clientId,
            recipientType: "client",
            title: notice.clientTitle,
            message: notice.clientMessage,
        });
    }
    return rows;
}
function evaluatePendingRevisionInvalidation(input) {
    if (!(0, revision_version_js_1.isRevisionSchemaV2)(input) || input.acceptanceStatus !== "pending") {
        return null;
    }
    if (isRevisionInvalidatedByLiveEvent(input.live, input.metadata, input.event)) {
        return { reason: revisionInvalidationNotice(input.event).reason };
    }
    const validation = (0, revision_diff_js_1.validateRevisionDiff)(input.live, input.metadata);
    if (!validation.ok) {
        return { reason: validation.errors.join("; ") };
    }
    return null;
}
function pendingRevisionFundWarning(input) {
    const pending = input.pendingRevisions.filter((revision) => (0, revision_version_js_1.isRevisionSchemaV2)(revision) && revision.acceptanceStatus === "pending");
    if (pending.length === 0) {
        return { pending: false, touchesThisInvoice: false, projectId: input.projectId };
    }
    const touchesThisInvoice = pending.some((revision) => isRevisionInvalidatedByLiveEvent(input.live, revision.metadata, { type: "invoice_paid", invoiceId: input.invoiceId }));
    return { pending: true, touchesThisInvoice, projectId: input.projectId };
}
exports.PENDING_REVISION_FUND_TOUCHED_BANNER = "This invoice is part of a pending revision. If you fund it as it is now, that revision will no longer be valid. The current project does not change. Review the revision first if you want those changes.";
exports.PENDING_REVISION_FUND_UNTOUCHED_BANNER = "There's a pending revision on this project. This payment does not affect it.";
exports.PENDING_REVISION_FUND_CONFIRM_TITLE = "Fund this invoice as it is now?";
exports.PENDING_REVISION_FUND_CONFIRM_BODY = "The pending revision will no longer be valid. The business will need to send an updated proposal.";
